import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, Video } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { createCallRequest } from "../../lib/adventureApi";
import { useMarket } from "../../lib/market";

const field = "h-11 rounded-sm bg-white border-sand-dark focus-visible:ring-terracotta";
const lbl = "font-mono text-[11px] uppercase tracking-widest text-ink-secondary mb-1.5 block";

function fmt(dt) {
  if (!dt) return null;
  try { return new Date(dt).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }); }
  catch { return dt; }
}

function Body({ tripName, onDone }) {
  const { market } = useMarket();
  const [f, setF] = useState({ name: "", email: "", phone: "", t1: "", t2: "", t3: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    const times = [f.t1, f.t2, f.t3].map(fmt).filter(Boolean);
    if (!f.name || !f.email) { toast.error("Please add your name and email"); return; }
    if (!times.length) { toast.error("Pick at least one preferred time"); return; }
    setSubmitting(true);
    try {
      await createCallRequest({ name: f.name, email: f.email, phone: f.phone, preferred_times: times, notes: f.notes, trip_name: tripName, market });
      setDone(true);
    } catch { toast.error("Something went wrong — please try again."); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="p-10 text-center" data-testid="call-success">
        <div className="w-14 h-14 rounded-full bg-moss-light flex items-center justify-center mx-auto"><Check className="w-7 h-7 text-moss" /></div>
        <h3 className="font-display font-extrabold text-2xl mt-4">Request sent!</h3>
        <p className="text-ink-secondary mt-2 max-w-sm mx-auto">Raj will confirm one of your preferred times by email and send a Google Meet link.</p>
        <button onClick={onDone} className="mt-6 bg-ink text-white h-11 px-6 rounded-sm font-semibold" data-testid="call-close-btn">Close</button>
      </div>
    );
  }

  return (
    <div className="p-6 max-h-[82vh] overflow-y-auto thin-scroll">
      <div className="flex items-center gap-3 mb-1">
        <img src="/podcast/raj.jpeg" alt="Raj" className="w-11 h-11 rounded-full object-cover border-2 border-terracotta" />
        <div>
          <p className="font-display font-extrabold text-lg tracking-tight leading-none">Meet Raj — virtual intro</p>
          <p className="text-[12px] text-ink-muted mt-1">A relaxed Google Meet with our founder. Pick a few times that suit you.</p>
        </div>
      </div>
      <div className="space-y-4 mt-5">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className={lbl}>Your name *</Label><Input data-testid="call-name" className={field} value={f.name} onChange={set("name")} /></div>
          <div><Label className={lbl}>Email *</Label><Input data-testid="call-email" type="email" className={field} value={f.email} onChange={set("email")} placeholder="example@email.com" /></div>
        </div>
        <div><Label className={lbl}>Phone (optional)</Label><Input data-testid="call-phone" className={field} value={f.phone} onChange={set("phone")} /></div>
        <div>
          <Label className={lbl}>Preferred times (pick 1–3)</Label>
          <div className="grid sm:grid-cols-3 gap-3">
            <Input data-testid="call-t1" type="datetime-local" className={field} value={f.t1} onChange={set("t1")} />
            <Input data-testid="call-t2" type="datetime-local" className={field} value={f.t2} onChange={set("t2")} />
            <Input data-testid="call-t3" type="datetime-local" className={field} value={f.t3} onChange={set("t3")} />
          </div>
        </div>
        <div><Label className={lbl}>Anything you'd like to cover?</Label><Textarea data-testid="call-notes" className="rounded-sm bg-white border-sand-dark focus-visible:ring-terracotta min-h-[80px]" value={f.notes} onChange={set("notes")} /></div>
        <button onClick={submit} disabled={submitting} data-testid="call-submit-btn"
          className="w-full h-12 rounded-sm bg-moss hover:bg-moss/90 text-white font-semibold tracking-widest uppercase text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Request the call</>}
        </button>
      </div>
    </div>
  );
}

export default function CallRequestForm({ tripName, triggerClassName, triggerLabel = "Book a virtual intro with Raj" }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="call-open-btn" className={triggerClassName}>
          <Video className="w-4 h-4" /> {triggerLabel}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg w-[95vw] p-0 gap-0 overflow-hidden rounded-md" data-testid="call-modal" aria-describedby={undefined}>
        <DialogTitle className="sr-only">Book a call with Raj</DialogTitle>
        {open && <Body tripName={tripName} onDone={() => setOpen(false)} />}
      </DialogContent>
    </Dialog>
  );
}
